import { addMinutes, format, formatDuration, isAfter, isBefore, isSameDay, startOfToday } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box, ClockOutline, Dropdown, DropdownInput, DropdownMenu, DropdownOption, Flex, Text } from '@bonusly/design-system';

interface TimeOption {
	value: string;
	label: string;
}

const generateTimeOptions = (start: Date, interval = 15): TimeOption[] => {
	const options: TimeOption[] = [];
	let current = start;
	while (isBefore(current, addMinutes(startOfToday(), 24 * 60))) {
		options.push({
			value: format(current, 'HH:mm'),
			label: format(current, 'h:mm a'),
		});
		current = addMinutes(current, interval);
	}
	return options;
};

const getTimeFromDate = (date: Date | null): string | null => {
	if (!date) return null;
	return format(date, 'HH:mm');
};

export const TimeInput = () => {
	const { t } = useTranslation('one_on_ones');
	const {
		control,
		formState: { errors: formErrors },
	} = useFormContext();
	const { field: startTimeField } = useController({
		name: 'startTime',
		control,
	});
	const { field: endTimeField } = useController({
		name: 'endTime',
		control,
	});
	const { field: dateField } = useController({ name: 'meetingDate', control });
	const [selectedStart, setSelectedStart] = useState<string | null>(getTimeFromDate(startTimeField.value));
	const [selectedEnd, setSelectedEnd] = useState<string | null>(getTimeFromDate(endTimeField.value));

	const today = startOfToday();
	const selectedDate = new Date(dateField.value); // Date from DateInput
	const isToday = isSameDay(selectedDate, today);

	const timeOptions = useMemo(() => generateTimeOptions(today), [today]);

	const now = new Date();
	const userSelectingForToday = isToday && !selectedStart && !selectedEnd;
	const currentHour = now.getHours() + 1;
	const defaultScrollIndex = userSelectingForToday ? 0 : currentHour * 4; // 4 * 15m units = 1h

	// When the date changes, update start and end times to use the new date
	useEffect(() => {
		if (selectedStart && startTimeField.value) {
			const [hours, minutes] = selectedStart.split(':').map(Number);
			const dateWithTime = new Date(selectedDate);
			dateWithTime.setHours(hours, minutes, 0, 0);
			startTimeField.onChange(dateWithTime);
		}
		if (selectedEnd && endTimeField.value) {
			const [hours, minutes] = selectedEnd.split(':').map(Number);
			const dateWithTime = new Date(selectedDate);
			dateWithTime.setHours(hours, minutes, 0, 0);
			endTimeField.onChange(dateWithTime);
		}
	}, [selectedDate.toDateString()]); // Only run when date portion changes

	const filteredStartTimeOptions = useMemo(
		() =>
			timeOptions.filter((option) => {
				// Prevent the user from selecting a start time that is too close to the current time
				// by adding a buffer of 15min to the current time
				// this is to give our BE processes time to run (create the data, etc)
				// before the meeting starts
				const nowWithBuffer = addMinutes(new Date(), 15);
				const optionTime = new Date(`${format(today, 'yyyy-MM-dd')}T${option.value}`);
				return !isToday || isAfter(optionTime, nowWithBuffer); // Filter out past times if today is selected
			}),
		[timeOptions, isToday, today]
	);

	const filteredEndTimeOptions = useMemo(
		() =>
			timeOptions.filter((option) => {
				const optionTime = new Date(`${format(today, 'yyyy-MM-dd')}T${option.value}`);
				const startTime = selectedStart ? new Date(`${format(today, 'yyyy-MM-dd')}T${selectedStart}`) : null;
				return startTime ? isAfter(optionTime, startTime) : true; // Ensure end time is after start time
			}),
		[timeOptions, selectedStart, today]
	);

	const duration = useMemo(() => {
		if (selectedStart && selectedEnd) {
			const startTime = new Date(`${format(today, 'yyyy-MM-dd')}T${selectedStart}`);
			const endTime = new Date(`${format(today, 'yyyy-MM-dd')}T${selectedEnd}`);
			const diff = (endTime.getTime() - startTime.getTime()) / (1000 * 60); // Difference in minutes
			const durObj = {
				hours: Math.floor(diff / 60),
				minutes: diff % 60,
			};
			const formatted = formatDuration(durObj, {
				format: ['hours', 'minutes'],
			}).replace(/\b(\d+)\s(minutes?|hours?|mins?|hrs?)\b/g, (match, num, unit) => {
				// Was only able to format duration as "X hours Y minutes"
				// using the formatDuration function from date-fns
				// this regex is to make it more concise
				if (unit.startsWith('min')) return `${num}min`;
				if (unit.startsWith('hour')) return `${num}h`;
				return match;
			});
			return formatted;
		}
		return null;
	}, [selectedStart, selectedEnd, today]);

	const selectTime = (field: 'start' | 'end', value: string) => {
		const [hours, minutes] = value.split(':').map(Number);
		const dateWithTime = new Date(selectedDate);
		dateWithTime.setHours(hours, minutes, 0, 0);

		if (field === 'start') {
			// preserve duration when changing start time
			if (selectedStart && selectedEnd && startTimeField.value && endTimeField.value) {
				const oldDuration = endTimeField.value.getTime() - startTimeField.value.getTime();
				const newEndTime = new Date(dateWithTime.getTime() + oldDuration);
				const newEndTimeString = format(newEndTime, 'HH:mm');

				setSelectedStart(value);
				startTimeField.onChange(dateWithTime);
				setSelectedEnd(newEndTimeString);
				endTimeField.onChange(newEndTime);
			} else {
				setSelectedStart(value);
				startTimeField.onChange(dateWithTime);
			}
		} else {
			setSelectedEnd(value);
			endTimeField.onChange(dateWithTime);
		}
	};

	return (
		<Flex flexDirection="row" justify="space-between" align="center" width="full" gap={1} py={1}>
			<Box flex="1 1 50%">
				<Dropdown
					matchWidth={true}
					onChange={(value) => selectTime('start', value)}
					renderOption={(value) => {
						const selected = filteredStartTimeOptions.find((o) => o.value === value);

						if (!selected) {
							return <Text color="text.softer">{t('meeting.modal.form.cadence.start_time')}</Text>;
						}

						return (
							<Flex justifyContent="left" w="full" gap={2}>
								<ClockOutline />
								<Text color="text.default">{selected.label}</Text>
							</Flex>
						);
					}}
					value={selectedStart}
				>
					<DropdownInput
						data-testid="starttime-input"
						minW={0}
						width="full"
						borderColor={formErrors && formErrors.startTime ? 'error.inputBorder' : 'border.default'}
					/>
					<DropdownMenu zIndex="popover" defaultScrollIndex={defaultScrollIndex}>
						{filteredStartTimeOptions.map((option) => (
							<DropdownOption key={option.value} value={option.value}>
								{option.label}
							</DropdownOption>
						))}
					</DropdownMenu>
				</Dropdown>
			</Box>
			<Text width="fit-content" color="text.softer">
				-
			</Text>
			<Box flex="1 1 50%">
				<Dropdown
					matchWidth={true}
					onChange={(value) => selectTime('end', value)}
					renderOption={(value) => {
						const selected = filteredEndTimeOptions.find((o) => o.value === value);

						if (!selected) {
							return <Text color="text.softer">{t('meeting.modal.form.cadence.end_time')}</Text>;
						}

						return (
							<Flex justifyContent="left" w="full" gap={2}>
								<Text color="text.default">{selected.label}</Text>
								<Text color="text.softest">{duration}</Text>
							</Flex>
						);
					}}
					value={selectedEnd}
				>
					<DropdownInput
						data-testid="endtime-input"
						minW={0}
						width="full"
						borderColor={formErrors && formErrors.endTime ? 'error.inputBorder' : 'border.default'}
					/>
					<DropdownMenu zIndex="popover" defaultScrollIndex={defaultScrollIndex}>
						{filteredEndTimeOptions.map((option) => (
							<DropdownOption key={option.value} value={option.value}>
								{option.label}
							</DropdownOption>
						))}
					</DropdownMenu>
				</Dropdown>
			</Box>
		</Flex>
	);
};
